import { getActiveUsers } from '@/data/user/selectors/activeUsers';
import { getDeletedUsers } from '@/data/user/selectors/getDeletedUsers';
import type { WiwState } from '@/store';

import { createSelector } from '@reduxjs/toolkit';
import { Map } from 'immutable';

const hasEmptyName = (user: any) => !user?.first_name || !user?.last_name;

export const getIgnoredRipplingUsers = (state: WiwState) =>
  state.integrations.rippling.configuration.get('users').ignored || Map();

export const getMatchedRipplingUsers = (state: WiwState) =>
  state.integrations.rippling.configuration.get('users').matched || Map();

// Rippling worker is similar to WIW person, one to many users
export const getRipplingWorkers = (state: WiwState) =>
  state.integrations.rippling.configuration.get('users').rippling || Map();

export const getUnmatchedUsers = createSelector(
  [getActiveUsers, getMatchedRipplingUsers, getIgnoredRipplingUsers, getDeletedUsers],
  (activeWIWUsers, matchedUsers, ignoredUsers, deletedUsers) => {
    const unmatchedUsers = activeWIWUsers.filter(user => !matchedUsers.has(user.id) && !ignoredUsers.has(user.id));

    return unmatchedUsers.filterNot((user, userId) => deletedUsers.has(userId) || hasEmptyName(user));
  },
);

export const matchedUsers = createSelector(
  [getActiveUsers, getMatchedRipplingUsers, getDeletedUsers],
  (activeWIWUsers, matchedRipplingUsers, deletedUsers) => {
    const matchedUsers = activeWIWUsers.filter(user => matchedRipplingUsers.has(user.id));

    return matchedUsers.filterNot((user, userId) => deletedUsers.has(userId) || hasEmptyName(user));
  },
);

export const ignoredUsers = createSelector(
  [getActiveUsers, getIgnoredRipplingUsers, getDeletedUsers],
  (activeWIWUsers, ignoredRipplingUsers, deletedUsers) => {
    const ignoredUsers = activeWIWUsers.filter(user => ignoredRipplingUsers.has(user.id));

    return ignoredUsers.filterNot((user, userId) => deletedUsers.has(userId) || hasEmptyName(user));
  },
);

export const getUnmatchedRipplingWorkers = createSelector(
  [getRipplingWorkers, getMatchedRipplingUsers, getIgnoredRipplingUsers],
  (ripplingWorkers, matchedRipplingUsers, ignoredRipplingUsers) => {
    const matchedRipplingIds = matchedRipplingUsers.valueSeq().toSet();
    const ignoredRipplingIds = ignoredRipplingUsers.valueSeq().toSet();
    return ripplingWorkers.filterNot(
      worker => matchedRipplingIds.contains(worker.id) || ignoredRipplingIds.contains(worker.id),
    );
  },
);
